import React, { useEffect, useMemo, useState } from "react";
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
  Box, Text, Input, Button, VStack, Select, Grid, Badge, HStack, Switch, FormControl,
  FormLabel, SimpleGrid, Alert, AlertIcon, Divider
} from "@chakra-ui/react";

const normalizeCarrier = (val = "") => {
  const v = String(val || "").toLowerCase();
  if (v.includes("fedex")) return "FedEx";
  if (v.includes("ups"))   return "UPS";
  if (v.includes("usps"))  return "USPS";
  if (v.includes("dhl"))   return "DHL";
  return "FedEx";
};

/**
 * PackingListInputModal
 *
 * ✅ Works for create & edit.
 * ✅ On edit, fetches existing packing list (admin_getPackingList) and PREFILLS:
 *    - carrier, tracking number
 *    - SKUs
 *    - shipping address default/override, toggle, and effective preview
 *
 * Props:
 *  - isOpen, onClose
 *  - order (must include order.graftsUsed; edit mode if order.packingId exists)
 *  - onSubmit({ carrier, trackingNumber, graftsWithSku, skus, useShippingOverride, shippingAddressOverride })
 *  - isSubmitting
 *  - getAuthToken: () => string   // required for secured endpoint call
 */
const PackingListInputModal = ({
  isOpen,
  onClose,
  order,
  onSubmit,
  isSubmitting = false,
  getAuthToken,
  // Bulk mode props
  isBulkMode = false,
  currentStep = null,
  totalSteps = null
}) => {
  const isEdit = Boolean(order?.packingId);

  // carrier/tracking
  const [carrier, setCarrier] = useState("FedEx");
  const [trackingNumber, setTrackingNumber] = useState("");

  // SKUs
  const [skuMap, setSkuMap] = useState({});

  // Address override model
  const [hydrating, setHydrating] = useState(false);
  const [useShippingOverride, setUseShippingOverride] = useState(false);
  const [defaultAddress, setDefaultAddress] = useState({ street: "", city: "", state: "", zip: "" });
  const [overrideAddress, setOverrideAddress] = useState({ street: "", city: "", state: "", zip: "" });
  const [facilityName, setFacilityName] = useState(""); // read-only label

  // ---- Grafts (explode to single units for SKU entry)
  const expandedGrafts = useMemo(() => {
    const grafts = Array.isArray(order?.graftsUsed) ? order.graftsUsed : [];
    return grafts.flatMap((g, originalIndex) =>
      Array.from({ length: g?.quantity || g?.units || 1 }, (_, i) => ({
        ...g,
        originalIndex,
        uniqueIndex: `${originalIndex}-${i}`,
      }))
    );
  }, [order]);

  const groupedByWound = useMemo(() => {
    return expandedGrafts.reduce((acc, g) => {
      const key = g?.woundIndex ?? 0;
      (acc[key] ||= []).push(g);
      return acc;
    }, {});
  }, [expandedGrafts]);

  // ---- Helpers (SKUs)
  const applySkusByIndex = (skus = []) => {
    const next = {};
    const min = Math.min(skus.length, expandedGrafts.length);
    for (let i = 0; i < min; i++) {
      const key = expandedGrafts[i].uniqueIndex;
      const sku = skus[i];
      if (sku) next[key] = String(sku);
    }
    setSkuMap(next);
  };

  const applySkusByMatching = (items = [], skus = []) => {
    const next = {};
    expandedGrafts.forEach((g) => {
      const matchIdx = items.findIndex((it) => {
        const itProd = (it.product || it.name || it.label || "").toLowerCase();
        const itSize = (it.size || "").toLowerCase();
        const gProd  = (g.product || g.productId || "").toLowerCase();
        const gSize  = (g.size || "").toLowerCase();
        const codeOk = !it.qCode || !g.qCode ? true : String(it.qCode).toLowerCase() === String(g.qCode).toLowerCase();
        return itProd.includes(gProd) && (itSize === "" || itSize === gSize) && codeOk;
      });
      if (matchIdx >= 0 && skus[matchIdx]) {
        next[g.uniqueIndex] = String(skus[matchIdx]);
      }
    });
    setSkuMap((prev) => ({ ...prev, ...next }));
  };

  // ---- Local prefill from order on open (for create)
  useEffect(() => {
    if (!isOpen || !order) return;

    setCarrier(normalizeCarrier(order.carrier));
    setTrackingNumber(order.trackingNumber || "");

    // SKUs on grafts (if present inline)
    const fromOrder = {};
    for (const g of expandedGrafts) {
      const sku = g?.sku || g?.SKU || g?.Sku;
      if (sku) fromOrder[g.uniqueIndex] = String(sku);
    }
    setSkuMap(fromOrder);

    // Default shipping address from order
    const ordAddr = order?.shippingAddress || {};
    setDefaultAddress({
      street: ordAddr.street || "",
      city: ordAddr.city || "",
      state: ordAddr.state || "",
      zip: ordAddr.zip || "",
    });
    setFacilityName(order?.facilityName || ""); // show if you pass it; otherwise the GET lambda provides snapshot
    setUseShippingOverride(false);
    setOverrideAddress({ street: "", city: "", state: "", zip: "" });
  }, [isOpen, order, expandedGrafts]);

  // ---- Server hydrate on edit (prefill everything)
  useEffect(() => {
    const hydrateFromServer = async () => {
      if (!isOpen || !isEdit || !order?.packingId) return;
      try {
        setHydrating(true);
        const token = typeof getAuthToken === "function" ? getAuthToken() : "";
        const res = await fetch(
          "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api/getPackingList",
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ packingId: order.packingId }),
          }
        );
        const data = await res.json();
        if (!res.ok || !data?.packingList) {
          console.warn("Failed to hydrate packing list", data);
          return;
        }

        const pl = data.packingList;

        // Carrier/Tracking
        if (pl.carrierName) setCarrier(normalizeCarrier(pl.carrierName));
        if (pl.trackingNumber) setTrackingNumber(pl.trackingNumber);

        // Address defaults + override + toggle
        const d = pl.shippingAddressDefaultSnapshot || pl.shippingAddressSnapshot || {};
        setDefaultAddress({
          street: d.street || "",
          city: d.city || "",
          state: d.state || "",
          zip: d.zip || "",
        });

        const o = pl.shippingAddressOverride || {};
        setOverrideAddress({
          street: o.street || "",
          city: o.city || "",
          state: o.state || "",
          zip: o.zip || "",
        });

        setUseShippingOverride(!!pl.useShippingOverride);
        setFacilityName(pl.facilityNameSnapshot || facilityName);

        // SKUs
        if (Array.isArray(pl.skus) && pl.skus.length) {
          applySkusByIndex(pl.skus);
          if (pl.items && pl.items.length !== expandedGrafts.length) {
            applySkusByMatching(pl.items, pl.skus);
          }
        } else if (Array.isArray(pl.items) && pl.items.length) {
          const candidateSkus = pl.items.map((it) => it.sku || it.SKU || it.Sku || "");
          applySkusByIndex(candidateSkus);
          applySkusByMatching(pl.items, candidateSkus);
        }
      } catch (err) {
        console.error("Hydrate (getPackingList) error:", err);
      } finally {
        setHydrating(false);
      }
    };

    hydrateFromServer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isEdit, order?.packingId]);

  // ---- Address helpers
  const effectiveAddress = useShippingOverride ? overrideAddress : defaultAddress;
  const formatAddress = (a) =>
    [facilityName, a.street, `${a.city}${a.city && (a.state || a.zip) ? "," : ""} ${a.state} ${a.zip}`.trim()]
      .filter((l) => (l || "").trim() !== "")
      .join("\n");

  const setOverrideField = (key, val) =>
    setOverrideAddress((prev) => ({ ...prev, [key]: val }));

  const resetOverrideToDefault = () => {
    setOverrideAddress({ ...defaultAddress });
  };

  // ---- Submit
  const handleSubmit = () => {
    const graftsWithSku = expandedGrafts.map((g) => ({
      ...g,
      sku: skuMap[g.uniqueIndex] || "",
    }));

    onSubmit?.({
      carrier,
      trackingNumber,
      graftsWithSku,
      skus: graftsWithSku.map((g) => g.sku || ""),
      useShippingOverride,
      shippingAddressOverride: useShippingOverride ? overrideAddress : null,
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {isEdit ? (
            <Box display="flex" alignItems="center" gap={8} flexWrap="wrap">
              <HStack spacing={3}>
                <Text>Edit Packing List</Text>
                {order?.packingId && (
                  <Badge colorScheme="purple" variant="subtle">P: {order.packingId}</Badge>
                )}
                {isBulkMode && currentStep && totalSteps && (
                  <Badge colorScheme="blue" variant="outline">
                    Step {currentStep} of {totalSteps}
                  </Badge>
                )}
              </HStack>
            </Box>
          ) : (
            <Box>
              <Text>Create Packing List</Text>
              {isBulkMode && currentStep && totalSteps && (
                <Text fontSize="sm" color="gray.600" mt={1}>
                  Processing step {currentStep} of {totalSteps} orders
                </Text>
              )}
            </Box>
          )}
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={6} align="stretch">
            {/* ---- Ship To (Override Toggle & Fields) */}
            <Box borderWidth="1px" borderRadius="xl" p={4} bg="gray.50" _dark={{ bg: "gray.800", borderColor: "gray.700" }} opacity={hydrating ? 0.6 : 1}>
              <HStack justify="space-between" align="center" mb={3}>
                <Text fontWeight="semibold">Ship To</Text>
                <FormControl display="flex" alignItems="center" width="auto">
                  <FormLabel htmlFor="overrideSwitch" mb="0" fontWeight="medium">
                    Use Custom Address
                  </FormLabel>
                  <Switch
                    id="overrideSwitch"
                    isChecked={useShippingOverride}
                    onChange={(e) => setUseShippingOverride(e.target.checked)}
                    isDisabled={hydrating}
                  />
                </FormControl>
              </HStack>

              {useShippingOverride && (
                <Alert status="warning" mb={3} borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={2}>
                    <Text>
                      Using a <strong>custom shipping address</strong>. You can edit the fields below.
                    </Text>
                    <Button 
                      variant="link" 
                      size="sm" 
                      colorScheme="orange"
                      onClick={resetOverrideToDefault}
                    >
                      Revert To Original
                    </Button>
                  </VStack>
                </Alert>
              )}

              {/* Address fields */}
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                <Box>
                  <Text fontSize="sm" mb={1}>Street</Text>
                  <Input
                    value={useShippingOverride ? overrideAddress.street : defaultAddress.street}
                    isDisabled={!useShippingOverride || hydrating}
                    onChange={(e) => setOverrideField("street", e.target.value)}
                    placeholder="123 Main St"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>City</Text>
                  <Input
                    value={useShippingOverride ? overrideAddress.city : defaultAddress.city}
                    isDisabled={!useShippingOverride || hydrating}
                    onChange={(e) => setOverrideField("city", e.target.value)}
                    placeholder="Anytown"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>State</Text>
                  <Input
                    value={useShippingOverride ? overrideAddress.state : defaultAddress.state}
                    isDisabled={!useShippingOverride || hydrating}
                    onChange={(e) => setOverrideField("state", e.target.value)}
                    placeholder="CA"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Text fontSize="sm" mb={1}>ZIP</Text>
                  <Input
                    value={useShippingOverride ? overrideAddress.zip : defaultAddress.zip}
                    isDisabled={!useShippingOverride || hydrating}
                    onChange={(e) => setOverrideField("zip", e.target.value)}
                    placeholder="90210"
                    size="sm"
                  />
                </Box>
              </SimpleGrid>

              {/* Effective preview */}
              <Divider my={4} />
              <Box>
                <Text fontWeight="medium" mb={1}>Effective Ship-To (preview)</Text>
                <Box
                  fontSize="sm"
                  whiteSpace="pre-line"
                  p={3}
                  borderWidth="1px"
                  borderRadius="md"
                  bg="white"
                  _dark={{ bg: "gray.900", borderColor: "gray.700" }}
                >
                  {formatAddress(effectiveAddress) || "—"}
                </Box>
              </Box>
            </Box>

            {/* ---- SKU Entry by wound */}
            {Object.entries(groupedByWound).map(([woundIdx, grafts]) => (
              <Box
                key={woundIdx}
                borderWidth="1px"
                borderRadius="xl"
                p={4}
                bg="gray.50"
                _dark={{ bg: "gray.800", borderColor: "gray.700" }}
                opacity={hydrating ? 0.6 : 1}
              >
                <Text fontWeight="semibold" fontSize="md" mb={3}>
                  Wound {parseInt(woundIdx) + 1}: {grafts[0]?.woundType || "—"}
                </Text>
                <VStack spacing={3} align="stretch">
                  {grafts.map((g) => (
                    <Grid
                      key={g.uniqueIndex}
                      templateColumns={{ base: "1fr", md: "3fr 1fr" }}
                      gap={3}
                      alignItems="center"
                    >
                      <Text fontSize="sm">
                        {g.product} ({g.qCode || g.productId || "—"}) • {g.size} • Qty: 1
                      </Text>
                      <Input
                        placeholder="SKU"
                        size="sm"
                        value={skuMap[g.uniqueIndex] || ""}
                        onChange={(e) => setSkuMap((prev) => ({ ...prev, [g.uniqueIndex]: e.target.value }))}
                        isDisabled={hydrating}
                        _hover={{ borderColor: "purple.400" }}
                      />
                    </Grid>
                  ))}
                </VStack>
              </Box>
            ))}

            {/* ---- Carrier / Tracking */}
            <Box opacity={hydrating ? 0.6 : 1}>
              <Text fontWeight="medium" mb={1}>Carrier</Text>
              <Select
                value={carrier}
                onChange={(e) => setCarrier(e.target.value)}
                size="sm"
                maxW="200px"
                isDisabled={hydrating}
              >
                <option>FedEx</option>
                <option>UPS</option>
                <option>USPS</option>
                <option>DHL</option>
              </Select>
            </Box>

            <Box opacity={hydrating ? 0.6 : 1}>
              <Text fontWeight="medium" mb={1}>Tracking Number</Text>
              <Input
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder="Optional"
                size="sm"
                isDisabled={hydrating}
              />
            </Box>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={onClose} mr={3} variant="ghost" isDisabled={isSubmitting || hydrating}>
            Cancel
          </Button>
          <Button
            colorScheme="purple"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText={isEdit ? "Updating..." : "Creating..."}
            isDisabled={hydrating}
            _hover={{ transform: "scale(1)" }}
          >
            {isEdit ? "Update Packing List" : "Submit Packing List"}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default PackingListInputModal;
